import React from 'react';
import { Button, TextField, Checkbox, Select, Box, Text, Heading, type SelectOption } from '@chatsdet/ui';
import type { ButtonVariant } from '@chatsdet/ui';
import { ForceInteractionState, type ForcedAttr } from './ForceInteractionState';

const STATES = ['Rest', 'Hover', 'Focus-visible', 'Pressed', 'Disabled'] as const;

type StateName = (typeof STATES)[number];

/**
 * Which states are forced via a React Aria attribute. `Rest` needs nothing, and
 * `Disabled` is a real prop on the control rather than a forced attribute.
 */
const FORCED_ATTR: Record<StateName, ForcedAttr | null> = {
  Rest: null,
  Hover: 'data-hovered',
  'Focus-visible': 'data-focus-visible',
  Pressed: 'data-pressed',
  Disabled: null,
};

const BUTTON_VARIANTS: { variant: ButtonVariant; label: string }[] = [
  { variant: 'primary', label: 'Primary' },
  { variant: 'secondary', label: 'Secondary' },
  { variant: 'outline', label: 'Outline' },
  { variant: 'ghost', label: 'Ghost' },
];

/**
 * Label column + one column per state, shared so the header and the rows can't
 * drift apart. The `5` must stay in step with `STATES` — it is written out
 * rather than interpolated because Tailwind only generates classes it can find
 * as literal text in the source; a template literal would silently produce no
 * grid at all.
 */
const GRID_COLS = 'grid grid-cols-[120px_repeat(5,1fr)] gap-2';

const SELECT_ITEMS: SelectOption[] = [
  { id: 'one', label: 'Option one' },
  { id: 'two', label: 'Option two' },
];

function StateCell({ children }: { children: React.ReactNode }) {
  return (
    <Box className="flex items-center justify-center p-3 min-h-[64px] min-w-[140px] border border-border/60 rounded-md">
      {children}
    </Box>
  );
}

function GridRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={`${GRID_COLS} items-center`}>
      <Text size="sm" weight="medium" className="pr-2">
        {label}
      </Text>
      {children}
    </div>
  );
}

function HeaderRow() {
  return (
    <div className={GRID_COLS}>
      <div />
      {STATES.map((state) => (
        <Text key={state} size="xs" color="muted" weight="medium" className="text-center">
          {state}
        </Text>
      ))}
    </div>
  );
}

/**
 * One row of the grid: the same control rendered once per state.
 *
 * `render` receives the state so the control can set `isDisabled` itself;
 * `selector` names the descendant that carries the RAC attribute when it isn't
 * the wrapper's first element child (e.g. TextField's `input`).
 */
function StateRow({
  label,
  selector,
  render,
}: {
  label: string;
  selector?: string;
  render: (state: StateName) => React.ReactNode;
}) {
  return (
    <GridRow label={label}>
      {STATES.map((state) => {
        const attr = FORCED_ATTR[state];
        return (
          <StateCell key={state}>
            {attr ? (
              <ForceInteractionState attr={attr} selector={selector}>
                {render(state)}
              </ForceInteractionState>
            ) : (
              render(state)
            )}
          </StateCell>
        );
      })}
    </GridRow>
  );
}

/**
 * Side-by-side rest/hover/focus-visible/pressed/disabled states for the core
 * interactive primitives, so one screenshot shows all of them at once.
 *
 * See `ForceInteractionState` for how the middle three are forced. Measured
 * limits, rather than assumed ones:
 *
 * - **Hover is inert for every row.** `Button`, `TextField`, `Select` and
 *   `Checkbox` contain zero `data-[hovered]` between them; all four write
 *   hover as Tailwind `hover:`, which compiles to the real pseudo-class and
 *   cannot be forced. Of the selectors this grid renders, only
 *   `button.bg-primary` and `button.bg-secondary` pair `[data-hovered]`
 *   (`button-chrome.css:50`, `:69`, `:99`, `:116`) — so exactly two rows
 *   (Primary, Secondary) show real forced hover. (`icon-buttons.css` and
 *   `navigation.css` pair it too, for elements the grid doesn't render.)
 * - **Focus-visible** is the same shape: attribute-driven only via
 *   `controls.css`, plus `Checkbox`'s `group-data-[focus-visible]:`.
 * - **Pressed** exists only on `Button`. TextField, Select and Checkbox model no
 *   pressed state at all, so those Pressed cells match Rest by definition.
 *
 * So the forced columns are a floor: an unchanged cell means "not forceable
 * here," not "unstyled." Pairing the RAC attribute alongside `hover:` in the
 * base components would fix both this grid and screenshot-based state
 * verification.
 */
export function InteractionStateGrid() {
  return (
    <div className="space-y-4">
      <Heading level={3}>Interaction States</Heading>
      <Text size="sm" color="muted" className="max-w-2xl">
        Hover, Focus-visible and Pressed are <em>forced</em> React Aria
        attributes, so they show only attribute-driven styling. Measured: every
        control here writes hover as real <code>:hover</code>, so the Hover
        column is inert everywhere except Primary and Secondary buttons, which
        pair <code>[data-hovered]</code>. Only Button has a pressed state
        at all. Treat these columns as a floor — an unchanged cell means
        &ldquo;not forceable here&rdquo;, not &ldquo;unstyled&rdquo;.
      </Text>
      <div className="overflow-x-auto">
        <div className="space-y-3 min-w-[900px]">
          <HeaderRow />
          {BUTTON_VARIANTS.map(({ variant, label }) => (
            <StateRow
              key={variant}
              label={label}
              render={(state) => (
                <Button variant={variant} size="sm" isDisabled={state === 'Disabled'}>
                  {label}
                </Button>
              )}
            />
          ))}
          <StateRow
            label="TextField"
            selector="input"
            render={(state) => (
              <TextField
                aria-label={state}
                placeholder={state}
                isDisabled={state === 'Disabled'}
              />
            )}
          />
          <StateRow
            label="Checkbox"
            render={(state) => <Checkbox aria-label={state} isDisabled={state === 'Disabled'} />}
          />
          <StateRow
            label="Select"
            selector="button"
            render={(state) => (
              <Select
                aria-label={state}
                items={SELECT_ITEMS}
                placeholder={state}
                isDisabled={state === 'Disabled'}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
